import { ACTIVE_OAUTH_STATE_STORAGE_KEY, EpicAuthClient } from './services/EpicAuthClient'

export type LaunchMode = 'standalone' | 'epic_smart'

/**
 * Returns the currently active Epic OAuth state string, or null.
 *
 * Resolution order:
 *  1. `?state=` URL query param — present only on the OAuth callback page.
 *  2. `sessionStorage[ACTIVE_OAUTH_STATE_STORAGE_KEY]` — persisted after a
 *     successful token exchange so that page reloads and deep links within the
 *     same tab can still resolve the correct session.
 *  3. `EpicAuthClient` in-memory sessions (backed by localStorage) — fallback
 *     for when sessionStorage was cleared by an Epic iframe re-navigation.
 *     Only used when exactly one session exists (unambiguous).
 */
export function getActiveEpicState(): string | null {
  if (typeof window === 'undefined') return null
  const urlState = new URLSearchParams(window.location.search).get('state')
  if (urlState) return urlState
  const sessionStorageState = sessionStorage.getItem(ACTIVE_OAUTH_STATE_STORAGE_KEY)
  if (sessionStorageState) return sessionStorageState
  // Fallback: sessionStorage was cleared (e.g. Epic iframe re-navigation within
  // the same tab, or a browser quirk).  Recover from localStorage where the
  // active state is also written during token exchange.
  const localStorageState = localStorage.getItem(ACTIVE_OAUTH_STATE_STORAGE_KEY)
  if (localStorageState) {
    sessionStorage.setItem(ACTIVE_OAUTH_STATE_STORAGE_KEY, localStorageState)
    return localStorageState
  }
  // Last resort: check if EpicAuthClient has exactly one session loaded from
  // localStorage (unambiguous which session is active).
  try {
    const recovered = EpicAuthClient.getInstance().getFirstSessionState()
    if (recovered) {
      sessionStorage.setItem(ACTIVE_OAUTH_STATE_STORAGE_KEY, recovered)
      return recovered
    }
  } catch {
    // EpicAuthClient not available or env vars missing
  }
  return null
}

/**
 * Returns the launch mode for the current browser session.
 *
 * - `epic_smart`: an active Epic OAuth state exists in sessionStorage (or the
 *   current URL) and the corresponding `EpicAuthClient` session is populated.
 * - `standalone`: fallback; user is (or will be) authenticated via Auth0.
 *
 * Safe to call outside React — will not throw even if `EpicAuthClient` has not
 * been constructed yet or if the required env vars are missing.
 */
export function getLaunchMode(): LaunchMode {
  const state = getActiveEpicState()
  if (!state) return 'standalone'
  try {
    return EpicAuthClient.getInstance().hasSession(state) ? 'epic_smart' : 'standalone'
  } catch {
    return 'standalone'
  }
}

/**
 * React hook that returns the current launch mode and the active Epic OAuth state.
 *
 * Returns `{ mode: 'standalone', epicState: null }` whenever there is no active
 * Epic session (including before the callback completes or after logout).
 */
export function useLaunchMode(): { mode: LaunchMode; epicState: string | null } {
  const epicState = getActiveEpicState()
  let hasEpicSession = false
  if (epicState) {
    try {
      hasEpicSession = EpicAuthClient.getInstance().hasSession(epicState)
    } catch {
      hasEpicSession = false
    }
  }
  const mode: LaunchMode = hasEpicSession ? 'epic_smart' : 'standalone'
  return { mode, epicState: mode === 'epic_smart' ? epicState : null }
}
